/**
 * Player Pick Adapter
 *
 * Transforms admin preview data into PlayerPick component props
 *
 * Note: PlayerPick uses the same interface as TeamPick, so this adapter
 * is essentially identical. The component internally treats the "teams"
 * as players with team context.
 */

import { createTeamsFromOptions } from '@low6-pickem/ui/utils';
import { formatPlayerNameAsInitialSurname } from '@low6-pickem/ui/utils/name-format';
import type { PreviewQuestionData } from '../types';

/**
 * PlayerPick component props (from packages/ui/src/components/PlayerPick.tsx).
 *
 * PlayerPick reads its candidates from an `options` array (Team-shaped). It is
 * NOT the same prop name as TeamPick (`teams`) — passing `teams` here leaves
 * `options` undefined and crashes in createJerseyMap.
 */
interface PlayerPickProps {
  questionId?: string;
  questionText: string;
  subtext?: string;
  options: any[]; // Team-shaped candidates (PlayerPick supports any count)
  selectedTeam?: string;
  onTeamSelect: (teamId: string | undefined) => void;
  points: number;
  earnedPoints?: number;
  isCorrect?: boolean;
  disabled?: boolean;
  className?: string;
  questionNumber?: number;
  questionMetadata?: {
    dailyDouble?: boolean;
  };
}

/**
 * Transforms preview data into PlayerPick props
 *
 * Creates "Team" objects from the player options. PlayerPick uses the same
 * Team structure as TeamPick but renders them in a player context and, unlike
 * TeamPick's fixed two teams, supports any number of candidates.
 *
 * @param data - Preview question data from admin form
 * @returns PlayerPick component props
 */
export function playerPickAdapter(data: PreviewQuestionData): PlayerPickProps {
  const language = data.language || 'en';

  // Create Team objects from every option (representing players), then apply
  // the same presentation the frontend's getPlayersForQuestion produces:
  // "Initial. Surname" names and the player's position (read from the
  // option-level grading data written by the player picker).
  const options = createTeamsFromOptions(data.options, language).map((team, index) => ({
    ...team,
    name: formatPlayerNameAsInitialSurname(team.name) || team.name,
    position: data.options[index]?.grading_data?.playerPosition,
  }));

  // A selection question needs at least two candidates to be meaningful.
  if (options.length < 2) {
    throw new Error('PlayerPick requires at least 2 options');
  }

  return {
    questionId: 'preview',
    questionText: data.questionText || 'Select a player',
    subtext: data.questionSubtext,
    options, // Team-shaped candidates, read by PlayerPick as `options`
    selectedTeam: undefined, // No selection in preview
    onTeamSelect: () => {}, // No-op handler (preview is view-only)
    points: data.totalPoints,
    disabled: true, // Preview is view-only
    questionNumber: undefined, // No question number in preview
  };
}
